import { getHistoricalData } from "@/_core/data/api_calls/candle_data/getHistoricalData";
import { formatDate_YMDHMS, roundDateToFloor } from "@/_core/_helpers/_helpers";
import gql from "graphql-tag";

/**
 * Retrieves historical technical analysis data for currencies.
 * It first attempts to fetch data from Redis; if unavailable, it queries the database.
 *
 * @param {object} _this - The context object containing necessary parameters.
 * @param {object} [assetExtraParams={}] - Optional parameters, including the currency number.
 * @returns {Promise<object>} - A promise resolving to the historical data.
 */
export function getCandleTechnicalAnalysisCurrencies(
  _this,
  assetExtraParams = {}
) {
  let fast = true; // Determines whether to use Redis caching or query the database directly.
  if (fast) {
    // Define the GraphQL query for retrieving data from Redis.
    const paramsRedis = gql`
      query($key: String!) {
        getRedisData(key: $key)
      }
    `;
    // Construct the Redis key based on asset parameters or context values.
    const variablesRedis = {
      format: "json",
      key:
        assetExtraParams &&
        Object.keys(assetExtraParams).length === 0 &&
        Object.getPrototypeOf(assetExtraParams) === Object.prototype
          ? "CUR_HIST_" + _this.currencyNumber.toString()
          : "CUR_HIST_" + assetExtraParams.currencyNumber.toString(),
      observation_date: formatDate_YMDHMS(
        roundDateToFloor(_this.initCurrencyDate)
      )
    };
    // Attempt to fetch data from Redis first.
    return getHistoricalData(_this, {
      params: paramsRedis,
      variables: variablesRedis,
      source: "getRedisData"
    }).catch(error => {
      console.error("Error from getRedisData:", error);
      // If Redis data retrieval fails, fall back to querying the technical analysis database.
      const paramsTechnicalAnalysis = gql`
        query($observation_date: String!, $currency: Int!, $format: String) {
          technical_analysis_historical(
            observation_date: $observation_date
            currency: $currency
            format: $format
          ) {
            observation_date
            open
            high
            low
            close
            settle
            ha_open
            ha_high
            ha_low
            ha_close
            avg_gain
            avg_loss
            bband_l1
            bband_l2
            bband_u1
            bband_u2
            bband_ave
            cluster
            ewma
            find_max_long
            find_max_medium
            find_max_short
            find_min_long
            find_min_medium
            find_min_short
            fk_idcurrency
            interp_trend_long
            interp_trend_medium
            interp_trend_short
            ma20
            ma40
            ma_40
            mvol
            pattern_dist
            pattern_selected
            pk_idcurrencyhistorical
            rsi
            y_pattern
          }
        }
      `;
      // Define the parameters for querying the database.
      const variablesTechnicalAnalysis = {
        format: "json",
        currency:
          assetExtraParams &&
          Object.keys(assetExtraParams).length === 0 &&
          Object.getPrototypeOf(assetExtraParams) === Object.prototype
            ? _this.currencyNumber
            : assetExtraParams.currencyNumber,
        observation_date: formatDate_YMDHMS(
          roundDateToFloor(_this.initCurrencyDate)
        )
      };
      // Fetch the technical analysis data if Redis is unavailable.
      return getHistoricalData(_this, {
        params: paramsTechnicalAnalysis,
        variables: variablesTechnicalAnalysis,
        source: "technical_analysis_historical"
      }).catch(error => {
        console.error("Error from technical_analysis_historical:", error);
        throw error;
      });
    });
  } else {
    console.error("Not using Redis, Warning!"); // Log a warning when Redis is not used.
    // Directly query the database if Redis is disabled.
    const paramsTechnicalAnalysis = gql`
      query($observation_date: String!, $currency: Int!, $format: String) {
        technical_analysis_historical(
          observation_date: $observation_date
          currency: $currency
          format: $format
        ) {
          observation_date
          open
          high
          low
          close
          settle
          ha_open
          ha_high
          ha_low
          ha_close
          avg_gain
          avg_loss
          bband_l1
          bband_l2
          bband_u1
          bband_u2
          bband_ave
          cluster
          ewma
          find_max_long
          find_max_medium
          find_max_short
          find_min_long
          find_min_medium
          find_min_short
          fk_idcurrency
          interp_trend_long
          interp_trend_medium
          interp_trend_short
          ma20
          ma40
          ma_40
          mvol
          pattern_dist
          pattern_selected
          pk_idcurrencyhistorical
          rsi
          y_pattern
        }
      }
    `;
    // Define the query parameters for the direct database request.
    const variablesTechnicalAnalysis = {
      format: "json",
      currency:
        assetExtraParams &&
        Object.keys(assetExtraParams).length === 0 &&
        Object.getPrototypeOf(assetExtraParams) === Object.prototype
          ? _this.currencyNumber
          : assetExtraParams.currencyNumber,
      observation_date: formatDate_YMDHMS(
        roundDateToFloor(_this.initCurrencyDate)
      )
    };
    // Fetch data directly from the database.
    return getHistoricalData(_this, {
      params: paramsTechnicalAnalysis,
      variables: variablesTechnicalAnalysis,
      source: "technical_analysis_historical"
    }).catch(error => {
      console.error("Error from technical_analysis_historical:", error);
      throw error;
    });
  }
}
